import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Button,
  Flex,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  Text,
  Select,
  Input,
  FormControl,
  FormLabel,
  Badge,
  Icon
} from "@chakra-ui/react";
import { UserIcon, RefreshIcon } from "../../utils/icons";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";

const UserStatsTab = ({ userLevel }) => {
  const [analysisStats, setAnalysisStats] = useState({
    userStats: {
      confirmed: 0,
      rejected: 0,
      pending: 0,
      totalReviewed: 0,
      batchesCompleted: 0
    },
    allUsersStats: [],
    topInfractionTypes: [],
    topCameras: []
  });
  const [loadingAnalysisStats, setLoadingAnalysisStats] = useState(false);
  
  // Estados para filtros de período
  const [periodFilter, setPeriodFilter] = useState('total');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [daysCount, setDaysCount] = useState(7);
  
  // Estados para ordenação da tabela de usuários
  const [sortConfig, setSortConfig] = useState({
    key: 'totalReviewed',
    direction: 'desc'
  });
  
  const toast = useToast();

  const fetchAnalysisStats = async (period = periodFilter, customDates = null, days = null) => {
    setLoadingAnalysisStats(true);
    try {
      const token = localStorage.getItem('token');
      
      // Para estatísticas totais, usar a rota original
      if (period === 'total') {
        const response = await fetch('/api/infractions/analysis-stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error('Erro ao buscar estatísticas de análise');
        const data = await response.json();
        setAnalysisStats(data);
        return;
      }
      
      // Para períodos específicos, construir parâmetros da query
      let queryParams = new URLSearchParams();
      
      if (period === 'custom' && customDates) {
        queryParams.append('period', 'custom');
        queryParams.append('startDate', customDates.startDate);
        queryParams.append('endDate', customDates.endDate);
      } else if (period === 'days' && days) {
        queryParams.append('period', 'days');
        queryParams.append('days', days.toString());
      } else {
        queryParams.append('period', period);
      }
      
      const response = await fetch(`/api/infractions/stats-by-period?${queryParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar estatísticas por período');
      const data = await response.json();
      
      // Adaptar os dados da API de período para o formato esperado
      const adaptedData = {
        userStats: {
          confirmed: data.stats?.confirmed || 0,
          rejected: data.stats?.rejected || 0,
          pending: data.stats?.pending || 0,
          totalReviewed: (data.stats?.confirmed || 0) + (data.stats?.rejected || 0),
          batchesCompleted: 0 // Não disponível na API de período
        },
        allUsersStats: [], // Não disponível para períodos específicos
        topInfractionTypes: data.byAlgorithm?.map(item => ({
          algorithmId: item.algorithmId,
          algorithmName: item.algorithmName,
          count: item.total || item.count
        })) || [],
        topCameras: data.byCamera?.map(item => ({
          cameraId: item.cameraId,
          cameraName: item.cameraName,
          count: item.total || item.count
        })) || [],
        period: data.period || period
      };
      
      setAnalysisStats(adaptedData);
      
    } catch (error) {
      console.error('Erro ao buscar estatísticas de análise:', error);
      toast({
        title: "Erro ao carregar estatísticas",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingAnalysisStats(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriodFilter(newPeriod);
    
    if (newPeriod === 'custom') {
      // Não fazer fetch automático para período customizado
      return;
    } else if (newPeriod === 'days') {
      fetchAnalysisStats(newPeriod, null, daysCount);
    } else {
      fetchAnalysisStats(newPeriod);
    }
  };

  const handleCustomDateSearch = () => {
    if (!customStartDate || !customEndDate) {
      toast({
        title: "Datas obrigatórias",
        description: "Por favor, selecione tanto a data inicial quanto a final",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    fetchAnalysisStats('custom', { startDate: customStartDate, endDate: customEndDate });
  };

  const handleDaysCountChange = (newDaysCount) => {
    setDaysCount(newDaysCount);
    if (periodFilter === 'days') {
      fetchAnalysisStats('days', null, newDaysCount);
    }
  };

  const getPeriodLabel = () => {
    if (analysisStats.period) {
      return analysisStats.period;
    }
    
    switch (periodFilter) {
      case 'today': return 'Hoje';
      case 'yesterday': return 'Ontem';
      case 'week': return 'Esta semana';
      case 'month': return 'Este mês';
      case 'year': return 'Este ano';
      case 'days': return `Últimos ${daysCount} dias`;
      case 'custom': return `${customStartDate} até ${customEndDate}`;
      default: return 'Total geral';
    }
  };

  // Função para ordenar dados - CORRIGIDA
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch (key) {
        case 'username':
          aValue = a.username?.toLowerCase() || '';
          bValue = b.username?.toLowerCase() || '';
          // Para strings, usar comparação de string
          if (direction === 'asc') {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        case 'confirmed':
          aValue = parseInt(a.confirmed) || 0;
          bValue = parseInt(b.confirmed) || 0;
          break;
        case 'rejected':
          aValue = parseInt(a.rejected) || 0;
          bValue = parseInt(b.rejected) || 0;
          break;
        case 'totalReviewed':
          aValue = parseInt(a.totalReviewed) || 0;
          bValue = parseInt(b.totalReviewed) || 0;
          break;
        case 'batchesCompleted':
          aValue = parseInt(a.batchesCompleted) || 0;
          bValue = parseInt(b.batchesCompleted) || 0;
          break;
        case 'confirmationRate':
          const totalA = parseInt(a.totalReviewed) || 0;
          const totalB = parseInt(b.totalReviewed) || 0;
          aValue = totalA > 0 ? ((parseInt(a.confirmed) || 0) / totalA) * 100 : 0;
          bValue = totalB > 0 ? ((parseInt(b.confirmed) || 0) / totalB) * 100 : 0;
          break;
        default:
          return 0;
      }
      
      // Para valores numéricos
      if (direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  };

  // Função para lidar com clique no cabeçalho da tabela
  const handleSort = (key) => {
    let direction = 'desc';
    
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  // Obter dados ordenados
  const getSortedUsersStats = () => {
    if (!analysisStats.allUsersStats || analysisStats.allUsersStats.length === 0) {
      return [];
    }
    
    return sortData(analysisStats.allUsersStats, sortConfig.key, sortConfig.direction);
  };

  // Componente para cabeçalho ordenável
  const SortableHeader = ({ children, sortKey, isNumeric = false }) => (
    <Th 
      isNumeric={isNumeric}
      cursor="pointer"
      onClick={() => handleSort(sortKey)}
      _hover={{ bg: 'gray.50' }}
      userSelect="none"
      position="relative"
    >
      <Flex align="center" justify={isNumeric ? 'flex-end' : 'flex-start'}>
        {children}
        <Box ml={1}>
          {sortConfig.key === sortKey ? (
            <Icon 
              as={sortConfig.direction === 'asc' ? ChevronUpIcon : ChevronDownIcon}
              color="blue.500"
              w={4}
              h={4}
            />
          ) : (
            <Icon 
              as={ChevronUpIcon}
              color="gray.300"
              w={4}
              h={4}
              opacity={0.5}
            />
          )}
        </Box>
      </Flex>
    </Th>
  );

  useEffect(() => {
    // Só fazer requisições se o usuário tiver nível superior a 2
    if (userLevel > 2) {
      fetchAnalysisStats();
    }
  }, [userLevel]);

  // Verificar se o usuário tem permissão para ver estatísticas
  if (userLevel <= 2) {
    return (
      <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md" mb={6}>
        <Flex justify="center" align="center" py={10}>
          <Text color="gray.500" fontSize="lg">
            Você não tem permissão para visualizar estatísticas de usuários.
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md" mb={6} w="100%" maxW="100%" overflowX="hidden">
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={3} mb={6} minW={0}>
        <Heading size="lg" display="flex" alignItems="center" flexWrap="wrap" fontSize={{ base: "xl", md: "2xl" }} minW={0}>
          <UserIcon color="teal.500" />
          Estatísticas de Usuários
          {periodFilter !== 'total' && (
            <Badge ml={{ base: 0, md: 3 }} mt={{ base: 2, md: 0 }} colorScheme="blue" variant="subtle">
              {getPeriodLabel()}
            </Badge>
          )}
        </Heading>
        <Button
          size="sm"
          leftIcon={<RefreshIcon />}
          colorScheme="blue"
          onClick={() => fetchAnalysisStats(periodFilter, 
            periodFilter === 'custom' ? { startDate: customStartDate, endDate: customEndDate } : null,
            periodFilter === 'days' ? daysCount : null
          )}
          isLoading={loadingAnalysisStats}
          w={{ base: "100%", md: "auto" }}
        >
          Atualizar
        </Button>
      </Flex>

      {/* Controles de Filtro por Período */}
      <Box mb={6} p={4} bg="gray.50" borderRadius="md" borderWidth="1px" w="100%" maxW="100%" minW={0}>
        <FormControl mb={4}>
          <FormLabel fontWeight="medium">Período de Análise</FormLabel>
          <Flex gap={3} align={{ base: "stretch", md: "end" }} direction={{ base: "column", md: "row" }} wrap="wrap" minW={0}>
            <Box w={{ base: "100%", md: "220px" }}>
              <Select 
                value={periodFilter} 
                onChange={(e) => handlePeriodChange(e.target.value)}
                bg="white"
              >
                <option value="total">Total Geral</option>
                <option value="today">Hoje</option>
                <option value="yesterday">Ontem</option>
                <option value="week">Esta Semana</option>
                <option value="month">Este Mês</option>
                <option value="year">Este Ano</option>
                <option value="days">Últimos X Dias</option>
                <option value="custom">Período Personalizado</option>
              </Select>
            </Box>

            {/* Controle para "Últimos X Dias" */}
            {periodFilter === 'days' && (
              <Box w={{ base: "100%", md: "auto" }}>
                <FormLabel fontSize="sm" mb={1}>Quantidade de dias</FormLabel>
                <Select
                  value={daysCount}
                  onChange={(e) => handleDaysCountChange(parseInt(e.target.value))}
                  bg="white"
                  w={{ base: "100%", md: "120px" }}
                >
                  <option value={1}>1 dia</option>
                  <option value={3}>3 dias</option>
                  <option value={7}>7 dias</option>
                  <option value={15}>15 dias</option>
                  <option value={30}>30 dias</option>
                  <option value={60}>60 dias</option>
                  <option value={90}>90 dias</option>
                </Select>
              </Box>
            )}

            {/* Controles para período personalizado */}
            {periodFilter === 'custom' && (
              <>
                <Box w={{ base: "100%", md: "auto" }}>
                  <FormLabel fontSize="sm" mb={1}>Data Inicial</FormLabel>
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    bg="white"
                    w={{ base: "100%", md: "160px" }}
                  />
                </Box>
                <Box w={{ base: "100%", md: "auto" }}>
                  <FormLabel fontSize="sm" mb={1}>Data Final</FormLabel>
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    bg="white"
                    w={{ base: "100%", md: "160px" }}
                  />
                </Box>
                <Button
                  colorScheme="blue"
                  onClick={handleCustomDateSearch}
                  isLoading={loadingAnalysisStats}
                  size="sm"
                  w={{ base: "100%", md: "auto" }}
                >
                  Buscar
                </Button>
              </>
            )}
          </Flex>
        </FormControl>
      </Box>

      {loadingAnalysisStats ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" />
        </Flex>
      ) : (
        <>
          {/* Estatísticas do usuário atual */}
          <Box mb={6}>
            <Heading size="md" mb={4} color="teal.600">
              Suas Estatísticas {periodFilter !== 'total' && `- ${getPeriodLabel()}`}
            </Heading>

            <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing={4}>
              <Stat>
                <StatLabel>Confirmadas</StatLabel>
                <StatNumber color="green.500">{analysisStats.userStats?.confirmed || 0}</StatNumber>
              </Stat>

              <Stat>
                <StatLabel>Rejeitadas</StatLabel>
                <StatNumber color="red.500">{analysisStats.userStats?.rejected || 0}</StatNumber>
              </Stat>

              <Stat>
                <StatLabel>Pendentes</StatLabel>
                <StatNumber color="yellow.500">{analysisStats.userStats?.pending || 0}</StatNumber>
              </Stat>

              <Stat>
                <StatLabel>Total Analisadas</StatLabel>
                <StatNumber color="blue.500">{analysisStats.userStats?.totalReviewed || 0}</StatNumber>
              </Stat>

              <Stat>
                <StatLabel>
                  {periodFilter === 'total' ? 'Lotes Concluídos' : 'Taxa de Confirmação'}
                </StatLabel>
                <StatNumber color="purple.500">
                  {periodFilter === 'total' 
                    ? (analysisStats.userStats?.batchesCompleted || 0)
                    : `${analysisStats.userStats?.totalReviewed > 0 
                        ? ((analysisStats.userStats.confirmed / analysisStats.userStats.totalReviewed) * 100).toFixed(1)
                        : '0'
                      }%`
                  }
                </StatNumber>
                {periodFilter === 'total' && (
                  <StatHelpText>
                    Taxa: {analysisStats.userStats?.totalReviewed > 0 
                      ? `${((analysisStats.userStats.confirmed / analysisStats.userStats.totalReviewed) * 100).toFixed(1)}%`
                      : '0%'
                    }
                  </StatHelpText>
                )}
              </Stat>
            </SimpleGrid>
          </Box>

          {/* Estatísticas detalhadas - apenas para usuários com nível 3+ */}
          {userLevel >= 3 && (
            <>
              {/* Estatísticas por usuário - só mostrar no período total */}
              {periodFilter === 'total' && analysisStats.allUsersStats?.length > 0 && (
                <Box mb={6}>
                  <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }} gap={2} mb={4} minW={0}>
                    <Heading size="md" color="blue.600">
                      Estatísticas por Usuário
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Clique nos cabeçalhos para ordenar
                    </Text>
                  </Flex>

                  <TableContainer w="100%" maxW="100%" overflowX="auto" overflowY="hidden">
                    <Table variant="simple" size="sm" minW="680px">
                      <Thead>
                        <Tr>
                          <SortableHeader sortKey="username">Usuário</SortableHeader>
                          <SortableHeader sortKey="confirmed" isNumeric>Confirmadas</SortableHeader>
                          <SortableHeader sortKey="rejected" isNumeric>Rejeitadas</SortableHeader>
                          <SortableHeader sortKey="totalReviewed" isNumeric>Total</SortableHeader>
                          <SortableHeader sortKey="batchesCompleted" isNumeric>Lotes</SortableHeader>
                          <SortableHeader sortKey="confirmationRate" isNumeric>Taxa Conf.</SortableHeader>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {getSortedUsersStats().map((user) => {
                          const totalReviewed = parseInt(user.totalReviewed) || 0;
                          const confirmed = parseInt(user.confirmed) || 0;
                          const rejected = parseInt(user.rejected) || 0;
                          const batchesCompleted = parseInt(user.batchesCompleted) || 0;
                          const confirmationRate = totalReviewed > 0 ? ((confirmed / totalReviewed) * 100).toFixed(1) : '0';
                          
                          return (
                            <Tr key={user.userId} _hover={{ bg: 'gray.50' }}>
                              <Td fontWeight="medium">{user.username}</Td>
                              <Td isNumeric color="green.500">{confirmed}</Td>
                              <Td isNumeric color="red.500">{rejected}</Td>
                              <Td isNumeric color="blue.500">{totalReviewed}</Td>
                              <Td isNumeric color="purple.500">{batchesCompleted}</Td>
                              <Td isNumeric>
                                <Text color={totalReviewed > 0 ? "gray.700" : "gray.400"}>
                                  {confirmationRate}%
                                </Text>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Tipos de infrações e câmeras mais analisados */}
              <SimpleGrid columns={[1, null, 2]} spacing={6}>
                {/* Tipos de infrações mais analisados */}
                {analysisStats.topInfractionTypes?.length > 0 && (
                    <Box p={4} borderWidth="1px" borderRadius="md" minW={0}>
                    <Heading size="sm" mb={3} color="blue.600">
                      Tipos Mais Analisados {periodFilter !== 'total' && `- ${getPeriodLabel()}`}
                    </Heading>

                    {analysisStats.topInfractionTypes.map((type, index) => (
                        <Flex key={type.algorithmId || index} justify="space-between" mb={2} minW={0} gap={2}>
                          <Text fontSize="sm" flex="1" minW={0} wordBreak="break-word">{type.algorithmName}</Text>
                          <Text fontSize="sm" fontWeight="bold" flexShrink={0}>{type.count}</Text>
                      </Flex>
                    ))}
                  </Box>
                )}

                {/* Câmeras com mais infrações analisadas */}
                {analysisStats.topCameras?.length > 0 && (
                    <Box p={4} borderWidth="1px" borderRadius="md" minW={0}>
                    <Heading size="sm" mb={3} color="green.600">
                      Câmeras Mais Analisadas {periodFilter !== 'total' && `- ${getPeriodLabel()}`}
                    </Heading>

                    {analysisStats.topCameras.map((camera, index) => (
                        <Flex key={camera.cameraId || index} justify="space-between" mb={2} minW={0} gap={2}>
                          <Text fontSize="sm" flex="1" minW={0} wordBreak="break-word">{camera.cameraName}</Text>
                          <Text fontSize="sm" fontWeight="bold" flexShrink={0}>{camera.count}</Text>
                      </Flex>
                    ))}
                  </Box>
                )}
              </SimpleGrid>
            </>
          )}

          {/* Indicador quando não há dados no período selecionado */}
          {analysisStats.userStats?.totalReviewed === 0 && periodFilter !== 'total' && (
            <Box mt={6} p={4} bg="yellow.50" borderRadius="md" borderWidth="1px" borderColor="yellow.200">
              <Text color="yellow.700" fontWeight="medium">
                Nenhuma infração foi analisada no período selecionado: {getPeriodLabel()}
              </Text>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default UserStatsTab;